import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  InputNumber,
  Select,
  Space,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { ReactNode, useEffect, useState } from "react";
import DateIcon from "../../../assets/icons/DateIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import { dateFormat, dbDateFormat } from "../../../constants/constants";
import { useGetBusinessShiftsQuery } from "../../../features/businessShift/businessShiftApi";
import { useGetHolidaysByDateQuery } from "../../../features/holiday/holidayApi";
import {
  useAssignGeneralAttendanceMutation,
  useAssignToShiftMutation,
  useUnAssignedMutation,
} from "../../../features/shiftAssignToEmployee/shiftAssignToEmployeeApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { HolidayData, ModalComponentProps } from "../../../types";
import { ShiftAssignedDataType } from "../../../types/shiftAssignEmployee";
import {
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
} from "../../../utils/utils";
import CustomModal from "../../ui/CustomModal";
import AssignGeneralAttendanceModal from "./AssignGeneralAttendanceModal";

dayjs.extend(isSameOrBefore);

interface DataT {
  date: string;
  business_member_id: string;
  data: ShiftAssignedDataType;
}

export default function AssignShiftModal({
  handleClose,
  data,
  ...rest
}: ModalComponentProps<DataT>) {
  const [form] = Form.useForm();
  const [repeat, setRepeat] = useState(false);
  const [day, setDay] = useState<"days" | "weeks">("weeks");
  const [endDate, setEndDate] = useState<Dayjs | null>(
    dayjs(data?.date, dbDateFormat)
  );
  const {
    open,
    handleOpen,
    handleClose: handleAssignClose,
  } = useModal<undefined, "ASSIGN">();
  const {
    isError: isShiftError,
    error: shiftError,
    data: shiftData,
  } = useGetBusinessShiftsQuery({});
  const [assignToShift, { isLoading, isSuccess, isError, error }] =
    useAssignToShiftMutation();

  const [
    unAssigned,
    {
      isLoading: isUnAssignedLoading,
      isSuccess: isUnAssignedSuccess,
      isError: isUnAssignedError,
      error: unAssignedError,
    },
  ] = useUnAssignedMutation();
  const [
    assignGeneralAttendance,
    {
      isLoading: isAssignedGeneralLoading,
      isSuccess: isAssignedGeneralSuccess,
      isError: isAssignedGeneralError,
      error: assignedGeneralError,
    },
  ] = useAssignGeneralAttendanceMutation();
  const {
    isError: isHolidayError,
    error: holidayError,
    data: holidayData,
  } = useGetHolidaysByDateQuery(
    {
      params: {
        start_date: data?.date,
        end_date: endDate ? endDate?.format(dbDateFormat) : undefined,
      },
    },
    {
      skip: !data?.date || !endDate,
    }
  );
  useError(isShiftError, shiftError, "Get shift fail");
  useError(isUnAssignedError, unAssignedError, "Unassigned fail");
  useError(
    isAssignedGeneralError,
    assignedGeneralError,
    "Assigned general attendance fail"
  );
  useError(isError, error, "Shift assigned fail");
  useError(isHolidayError, holidayError, "Get holiday fail");

  const onFinish = (values: any) => {
    const { end_date, repeat_range, repeat_type, days, ...rest } = values;

    if (repeat && day === "weeks" && !days?.length) {
      return toast("error", "Please select at least one day for the week");
    }

    const newData = {
      ...rest,
      business_member_id: [data?.business_member_id],
      repeat: repeat ? 1 : 0,
      repeat_type: repeat_type || "days",
      repeat_range: !repeat ? 1 : repeat_range,
      start_date: data?.date,
      end_date: end_date ? dayjs(end_date).format(dbDateFormat) : data?.date,
      days: days || [],
      holidays: transformDates(holidayData?.data?.holidays),
      shift_settings: {
        days: days || [],
        repeat: repeat ? 1 : 0,
        repeat_type: repeat_type || "days",
        repeat_range: !repeat ? 1 : repeat_range,
        end_date: end_date ? dayjs(end_date).format(dbDateFormat) : data?.date,
      },
    };

    assignToShift(newData);
  };

  const handleUnAssigned = () => {
    const values = {
      start_date: data?.date,
      end_date: data?.date,
      business_member_ids: [data?.business_member_id],
    };

    unAssigned(values);
  };

  // general attendance handler
  const handleAssignedGeneral = () => {
    const getData = form.getFieldsValue();

    const values = {
      start_date: data?.date,
      end_date: getData?.end_date
        ? dayjs(getData?.end_date).format(dbDateFormat)
        : data?.date,
      business_member_id: [data?.business_member_id],
      repeat: repeat ? 1 : 0,
      // repeat_type: getData?.repeat_type || "days",
      repeat_type: "days",
      // repeat_range: !repeat ? 1 : getData?.repeat_range,
      repeat_range: 1,
      days: getData?.days || [],
      holidays: transformDates(holidayData?.data?.holidays),
      shift_settings: {
        days: getData?.days || [],
        repeat: repeat ? 1 : 0,
        end_date: getData?.end_date
          ? dayjs(getData?.end_date).format(dbDateFormat)
          : data?.date,
        // repeat_type: getData?.repeat_type || "days",
        repeat_type: "days",
        // repeat_range: !repeat ? 1 : getData?.repeat_range,
        repeat_range: 1,
      },
    };

    assignGeneralAttendance(values);
  };

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Shift assigned successfully");
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isUnAssignedSuccess) {
      toast("success", "Unassigned successfully");
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnAssignedSuccess]);

  useEffect(() => {
    if (isAssignedGeneralSuccess) {
      toast("success", "Assigned general attendance successfully");
      handleAssignClose();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssignedGeneralSuccess]);

  useEffect(() => {
    if (Object.keys(data?.data || {})) {
      const shiftSetting = data?.data?.shift_settings;
      form.setFieldsValue({
        shift_id: data?.data?.additional_settings?.shift_id,
        repeat: shiftSetting?.repeat,
        repeat_range: shiftSetting?.repeat_range,
        repeat_type: shiftSetting?.repeat_type,
        end_date: shiftSetting?.end_date
          ? dayjs(shiftSetting?.end_date, dbDateFormat)
          : null,
        days: shiftSetting?.days,
      });

      setRepeat(data?.data?.shift_settings?.repeat ? true : false);
      setDay(shiftSetting?.repeat_type || "weeks");
      setEndDate(
        shiftSetting?.end_date
          ? dayjs(shiftSetting?.end_date, dbDateFormat)
          : null
      );
    }
  }, [form, data?.data]);

  const disableDatesBefore = (current: Dayjs | null): boolean => {
    return current
      ? current.isBefore(dayjs(data?.date, dbDateFormat), "day")
      : false;
  };

  return (
    <CustomModal
      title={data?.data?.is_shift ? "Update Shift" : "Assign Shift"}
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <FormItem title="Shift">
          <Form.Item
            name="shift_id"
            className="mb-0"
            rules={[
              {
                required: true,
                message: "Please select shift",
              },
            ]}
          >
            <Select
              placeholder="Select Shift"
              size="middle"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                shiftData?.data?.shifts || [],
                "title",
                "id"
              )}
              suffixIcon={<SelectDropdownLgIcon />}
              allowClear
            />
          </Form.Item>
        </FormItem>

        <Form.Item name="repeat" className="mb-4">
          <Checkbox
            checked={repeat}
            onChange={(e) => setRepeat(e.target.checked)}
            className="text-header text-base font-medium select-none"
          >
            Repeat
          </Checkbox>
        </Form.Item>

        {repeat && (
          <>
            <FormItem title="Every">
              <div className="flex items-center gap-3">
                <Form.Item
                  name="repeat_range"
                  className="mb-0"
                  validateFirst
                  rules={[
                    {
                      required: true,
                      message: "Please select range",
                    },
                    {
                      type: "number",
                      min: 1,
                      message: "Range must be at least 1",
                    },
                    {
                      type: "integer",
                      message: "Range must be an integer",
                    },
                  ]}
                  initialValue={1}
                >
                  <InputNumber size="middle" style={{ width: 50 }} />
                </Form.Item>
                <Form.Item
                  name="repeat_type"
                  className="mb-0"
                  rules={[
                    {
                      required: true,
                      message: "Please select day",
                    },
                  ]}
                  initialValue={day}
                >
                  <Select
                    placeholder="Select"
                    size="middle"
                    filterOption={selectSearchOption}
                    options={selectStringOption(
                      ["days", "weeks"],
                      "string",
                      true
                    )}
                    suffixIcon={<SelectDropdownLgIcon />}
                    style={{ width: 134 }}
                    onChange={(val: "days" | "weeks") => setDay(val)}
                  />
                </Form.Item>
              </div>
            </FormItem>

            <FormItem title="End on">
              <Form.Item
                name="end_date"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "Please select date",
                  },
                ]}
                initialValue={dayjs(data?.date, dbDateFormat)}
              >
                <DatePicker
                  format={dateFormat}
                  suffixIcon={<DateIcon width={14} height={16} />}
                  style={{ width: "100%" }}
                  size="middle"
                  className="clear-button-hide"
                  disabledDate={disableDatesBefore}
                  onChange={setEndDate}
                />
              </Form.Item>
            </FormItem>
            {day === "weeks" && (
              <FormItem title="Week">
                <Form.Item
                  name="days"
                  className="mb-0"
                  // Uncomment the rules if you want validation
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Please select week",
                  //   },
                  // ]}
                >
                  <Checkbox.Group className="!inline-block">
                    {days.map((weekDay, index) => (
                      <Checkbox
                        key={index}
                        value={weekDay.value}
                        rootClassName="checkbox-week"
                      >
                        {weekDay.label}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              </FormItem>
            )}

            <div>
              {holidayData?.data?.holidays?.map((holiday, idx) => (
                <div className="flex gap-2" key={idx}>
                  <h6 className="whitespace-nowrap text-header font-medium">
                    {formatDate(holiday?.start_date)}

                    {holiday?.end_date &&
                    formatDate(holiday?.start_date) !==
                      formatDate(holiday?.end_date)
                      ? ` - ${formatDate(holiday?.end_date)}`
                      : ""}
                  </h6>
                  <div className="text-body">({holiday.title})</div>
                </div>
              ))}
            </div>
          </>
        )}

        {(data?.data?.is_general === 0 || !data?.data) && (
          <div className="p-4 bg-status-danger-bg rounded-md mt-1">
            <p className="self-stretch text-display-body text-sm mb-3">
              Want to move employee from shift management to general attendance
              system?
            </p>
            <Button
              className="outline-button-primary !h-9 !px-3 !text-sm !font-semibold"
              onClick={() =>
                handleOpen({
                  type: "ASSIGN",
                })
              }
            >
              Assign General Attendance
            </Button>
          </div>
        )}

        <Space className="w-full mt-6" wrap size={12}>
          {/* {data?.data?.is_general === 0 ? (
            <Button
              className="normal-dark-button modal-button"
              onClick={handleUnAssigned}
              htmlType="button"
              size="middle"
              loading={isUnAssignedLoading}
              disabled={isLoading}
            >
              Unassign
            </Button>
          ) : (
            <Button
              className="normal-dark-button modal-button"
              onClick={handleClose}
              htmlType="button"
              size="middle"
              disabled={isUnAssignedLoading || isLoading}
            >
              Close
            </Button>
          )} */}

          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            htmlType="button"
            size="middle"
            disabled={isLoading}
          >
            Close
          </Button>

          {data?.data ? (
            <Button
              className="download-button !h-auto"
              onClick={handleUnAssigned}
              htmlType="button"
              size="middle"
              loading={isUnAssignedLoading}
              disabled={isLoading}
            >
              Unassign
            </Button>
          ) : null}

          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
            // disabled={isUnAssignedLoading}
          >
            {data?.data?.is_shift ? "Update Shift" : "Assign Shift"}
          </Button>
        </Space>
      </Form>

      <AssignGeneralAttendanceModal
        open={open.open}
        handleClose={handleAssignClose}
        handleSubmit={handleAssignedGeneral}
        loading={isAssignedGeneralLoading}
        currentDate={data?.date}
      />
    </CustomModal>
  );
}

const days = [
  { label: "S", value: "sunday" },
  { label: "M", value: "monday" },
  { label: "T", value: "tuesday" },
  { label: "W", value: "wednesday" },
  { label: "T", value: "thursday" },
  { label: "F", value: "friday" },
  { label: "S", value: "saturday" },
];

interface FormItemProps {
  title: string;
  children: ReactNode;
}

const FormItem = ({ title, children }: FormItemProps) => (
  <div className="grid grid-cols-[60px_auto] items-center gap-3 mb-4">
    <h6 className="min-w-[60px] text-body text-base font-medium">{title}:</h6>
    {children}
  </div>
);

interface TransformedEvent {
  title: string;
  date: string;
}

export const transformDates = (input?: HolidayData[]): TransformedEvent[] => {
  const result: TransformedEvent[] = [];

  input?.forEach((item) => {
    const startDate: Dayjs | null = item?.start_date
      ? dayjs(item?.start_date)
      : null;
    const endDate: Dayjs | null = item?.end_date ? dayjs(item?.end_date) : null;

    if (startDate?.isSame(endDate, "day")) {
      result.push({ title: item.title, date: item?.start_date });
    } else {
      let currentDate: Dayjs | null = startDate;
      while (currentDate?.isSameOrBefore(endDate, "day")) {
        result.push({ title: item.title, date: currentDate.toISOString() });
        currentDate = currentDate.add(1, "day");
      }
    }
  });

  return result;
};
